import type { Modification } from 'src/services/modification/types';
import type {
  RegionTask,
  RegionTaskGroup,
  RegionTaskModification,
  RegionTasksAndGroupsDTO,
} from 'src/services/tasks/types';
import { TableRowTypes } from 'src/store/adminConfigPageSlice/regionTasksPageSlice/constants';
import type { TaskFormTableRow } from 'src/store/adminConfigPageSlice/regionTasksPageSlice/types';
import { arrayMove } from 'src/utils/helpers';

/**
 * If a task doesn't have a rank, move it to the top, otherwise sort based on
 * rank ascending.
 */
const sortTasks = (t1: TaskFormTableRow, t2: TaskFormTableRow) => {
  if (t1.rank == null) {
    return -1;
  } else if (t2.rank == null) {
    return 1;
  } else {
    return t1.rank - t2.rank;
  }
};

/**
 * Corner case when there is no tasks has rank and move select task to
 * bottom.
 */
const findHighestRankIndex = (arr: TaskFormTableRow[]): number => {
  for (let i = 0; i < arr.length; i++) {
    if (arr[i].rank === 1) {
      return i - 1;
    }
  }
  return arr.length - 1;
};

/**
 * Change task order, returning a new array.
 */
export const changeTaskOrder = (rows: TaskFormTableRow[], fromIndex: number, toIndex: number): TaskFormTableRow[] => {
  const arr = [...rows];
  // If moved row don't have a rank, auto move to the first rank
  let updatedRank = 1;
  let updatedIndex = findHighestRankIndex(arr);
  if (arr[toIndex].rank !== null) {
    updatedRank = arr[toIndex].rank ?? 0;
    updatedIndex = toIndex;
  }
  arrayMove(arr, fromIndex, updatedIndex);
  // Update task rank from start index and rank all the way to the end
  arr[updatedIndex].rank = updatedRank;
  const startIndex = arr.findIndex((val) => val.rank !== null);
  let startRank = 1;
  arr.forEach((item, index) => {
    if (index >= startIndex) {
      item.rank = startRank++;
    }
  });
  // Return
  return arr;
};

/**
 * Get a list of all unique child-tasks for a group.
 */
export const getSharedModifications = (group: RegionTaskGroup): RegionTaskModification[] => {
  if (group.tasks.length < 1) return [];
  let memo = new Set<number>();
  return group.tasks.reduce<RegionTaskModification[]>((acc, task) => {
    const toAdd = task.modifications.filter((m) => !memo.has(m.modificationId));
    memo = new Set([...memo, ...toAdd.map((m) => m.modificationId)]);
    return [...acc, ...toAdd];
  }, []);
};

/**
 * Columns for the modification matrix: one task per column. A plain task is its
 * own single column; a group has one column per child-task.
 */
export const getMatrixTasks = (row: TaskFormTableRow): RegionTask[] =>
  row.type === TableRowTypes.TaskGroup ? row.tasks : [row];

/**
 * Map the full modification catalog (redux/TanStack shape) into the
 * RegionTaskModification shape the editor works in. Every modification is
 * selectable, whether or not it's currently assigned.
 */
export const toRegionTaskModifications = (catalog: Modification[]): RegionTaskModification[] =>
  catalog.map((m) => ({ modificationId: m.id, name: m.name, description: m.description }));

/**
 * Rebuild a row from a matrix selection. `enabledByTask` maps a taskId to the
 * set of modificationIds enabled for that task; `modifications` is the catalog
 * the selection is drawn from. Each task keeps only the modifications enabled
 * for it; a group's commonModifications is recomputed.
 */
export const applyModificationMatrix = (
  row: TaskFormTableRow,
  enabledByTask: Record<number, Set<number>>,
  modifications: RegionTaskModification[],
): TaskFormTableRow => {
  const modificationsForTask = (taskId: number): RegionTaskModification[] => {
    const enabled = enabledByTask[taskId] ?? new Set<number>();
    return modifications.filter((m) => enabled.has(m.modificationId));
  };
  if (row.type === TableRowTypes.TaskGroup) {
    const tasks = row.tasks.map((task) => ({ ...task, modifications: modificationsForTask(task.taskId) }));
    return { ...row, tasks, commonModifications: getSharedModifications({ ...row, tasks }) };
  }
  return { ...row, modifications: modificationsForTask(row.taskId) };
};

/**
 * Convert a RegionTasksAndGroupsDTO into sorted table rows.
 */
export const mapDtoToRows = (dto: RegionTasksAndGroupsDTO): TaskFormTableRow[] => {
  const taskGroups: TaskFormTableRow[] = dto.taskGroups.map((group) => ({
    ...group,
    commonModifications: getSharedModifications(group),
    entityId: `g-${group.taskGroupId}`,
    type: 'task-group',
  }));
  const ungroupedTasks: TaskFormTableRow[] = dto.ungroupedTasks.map((task) => ({
    ...task,
    entityId: `t-${task.taskId}`,
    type: 'task',
  }));
  return [...taskGroups, ...ungroupedTasks].sort(sortTasks);
};
